import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { Mail, MessageSquare, ArrowLeft, Send, CheckCircle2, Clock, HelpCircle, AlertCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import ParticleBackground from '../components/ParticleBackground'

// Try to import supportConfig for email
import { supportConfig } from '../config/support'

// Security utilities
const RATE_LIMIT_KEY = 'contact_form_submissions'
const RATE_LIMIT_WINDOW = 60 * 60 * 1000 // 1 hour in milliseconds
const MAX_SUBMISSIONS_PER_HOUR = 3
const MIN_FORM_TIME = 5000 // Minimum 5 seconds to fill form (prevents instant bot submissions)

// Spam pattern detection
const spamPatterns = [
  /(http|https|www\.|\.com|\.net|\.org)/gi, // URLs
  /(viagra|cialis|casino|poker|loan|credit|debt)/gi, // Common spam keywords
  /(click here|buy now|limited time|act now)/gi, // Marketing phrases
  /[A-Z]{10,}/g, // Excessive caps
  /(.)\1{5,}/g, // Repeated characters
]

const checkSpam = (text: string): boolean => {
  return spamPatterns.some(pattern => pattern.test(text))
}

const checkRateLimit = (): { allowed: boolean; remainingTime?: number } => {
  const stored = localStorage.getItem(RATE_LIMIT_KEY)
  if (!stored) {
    return { allowed: true }
  }

  const data = JSON.parse(stored)
  const now = Date.now()
  const recentSubmissions = data.submissions.filter((time: number) => now - time < RATE_LIMIT_WINDOW)

  if (recentSubmissions.length >= MAX_SUBMISSIONS_PER_HOUR) {
    const oldestSubmission = Math.min(...recentSubmissions)
    const remainingTime = RATE_LIMIT_WINDOW - (now - oldestSubmission)
    return { allowed: false, remainingTime }
  }

  return { allowed: true }
}

const recordSubmission = (): void => {
  const stored = localStorage.getItem(RATE_LIMIT_KEY)
  const now = Date.now()

  if (!stored) {
    localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify({
      firstSubmission: now,
      submissions: [now]
    }))
  } else {
    const data = JSON.parse(stored)
    const recentSubmissions = data.submissions.filter((time: number) => now - time < RATE_LIMIT_WINDOW)
    recentSubmissions.push(now)
    
    localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify({
      firstSubmission: data.firstSubmission || now,
      submissions: recentSubmissions
    }))
  }
}

const Contact: React.FC = () => {
  const navigate = useNavigate()
  const formStartTime = useRef<number>(Date.now())
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    honeypot: '' // Honeypot field - bots will fill this
  })
  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset form start time when component mounts
  useEffect(() => {
    formStartTime.current = Date.now()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Security Check 1: Honeypot field (bots will fill this)
    if (formData.honeypot) {
      setError('Spam detected. Please try again.')
      return
    }

    // Security Check 2: Rate limiting
    const rateLimit = checkRateLimit()
    if (!rateLimit.allowed) {
      const minutes = Math.ceil((rateLimit.remainingTime || 0) / (60 * 1000))
      setError(`Too many submissions. Please wait ${minutes} minute${minutes > 1 ? 's' : ''} before trying again.`)
      return
    }

    // Security Check 3: Minimum form fill time (prevents instant bot submissions)
    const formFillTime = Date.now() - formStartTime.current
    if (formFillTime < MIN_FORM_TIME) {
      setError('Form submitted too quickly. Please take your time filling out the form.')
      return
    }

    // Security Check 4: Spam pattern detection
    const fullText = `${formData.name} ${formData.email} ${formData.subject} ${formData.message}`.toLowerCase()
    if (checkSpam(fullText)) {
      setError('Your message contains suspicious content. Please revise and try again.')
      return
    }

    // Security Check 5: Input validation
    if (formData.name.length < 2 || formData.name.length > 100) {
      setError('Name must be between 2 and 100 characters.')
      return
    }

    if (formData.message.length < 10 || formData.message.length > 2000) {
      setError('Message must be between 10 and 2000 characters.')
      return
    }

    if (formData.subject.length < 3 || formData.subject.length > 200) {
      setError('Subject must be between 3 and 200 characters.')
      return
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address.')
      return
    }

    setIsSubmitting(true)
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500))
      
      // Record successful submission for rate limiting
      recordSubmission()
      
      // In a real application, you would send this data to a backend API
      // with additional server-side validation and CSRF protection
      console.log('Form submitted:', {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        timestamp: new Date().toISOString(),
        formFillTime: formFillTime
      })
      
      setSubmitted(true)
      setIsSubmitting(false)
      
      // Reset form after success
      setTimeout(() => {
        setSubmitted(false)
        setFormData({ name: '', email: '', subject: '', message: '', honeypot: '' })
        formStartTime.current = Date.now()
      }, 5000)
    } catch (err) {
      setError('An error occurred. Please try again later.')
      setIsSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const contactEmail = supportConfig?.contact?.email || 'officialpsalm91studio@gmail.com'

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative">
      <ParticleBackground />
      
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-6xl mx-auto"
        >
          {/* Back Button */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-bible-gold hover:text-yellow-300 transition-colors mb-6 sm:mb-8 text-sm sm:text-base"
          >
            <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
            <span>Back to Home</span>
          </button>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-8 sm:mb-12"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-bible-gold mb-3 sm:mb-4">
              Contact Us
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-300 px-4">
              We'd love to hear from you
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-slate-800/50 backdrop-blur-sm rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-slate-700 order-2 lg:order-1"
            >
              <h2 className="text-xl sm:text-2xl font-bold text-bible-gold mb-6">Get in Touch</h2>
              
              <div className="space-y-5 sm:space-y-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2.5 sm:p-3 bg-bible-gold/20 rounded-lg flex-shrink-0">
                    <Mail className="text-bible-gold sm:w-6 sm:h-6" size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-1">Email</h3>
                    <a 
                      href={`mailto:${contactEmail}`}
                      className="text-sm sm:text-base text-gray-400 hover:text-bible-gold transition-colors break-all"
                    >
                      {contactEmail}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2.5 sm:p-3 bg-bible-gold/20 rounded-lg flex-shrink-0">
                    <MessageSquare className="text-bible-gold sm:w-6 sm:h-6" size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-1">Feedback</h3>
                    <p className="text-sm sm:text-base text-gray-400">We welcome your feedback and suggestions</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2.5 sm:p-3 bg-bible-gold/20 rounded-lg flex-shrink-0">
                    <Clock className="text-bible-gold sm:w-6 sm:h-6" size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-1">Response Time</h3>
                    <p className="text-sm sm:text-base text-gray-400">We typically respond within 24-48 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2.5 sm:p-3 bg-bible-gold/20 rounded-lg flex-shrink-0">
                    <HelpCircle className="text-bible-gold sm:w-6 sm:h-6" size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-1">Questions?</h3>
                    <p className="text-sm sm:text-base text-gray-400">Feel free to reach out with any questions</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-slate-700">
                <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">
                  Whether you have questions about features, suggestions for improvements, 
                  or want to report an issue, we're here to help. Your input helps us make 
                  this platform better for everyone.
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-slate-800/50 backdrop-blur-sm rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-slate-700 order-1 lg:order-2"
            >
              <h2 className="text-xl sm:text-2xl font-bold text-bible-gold mb-6">Send a Message</h2>
              
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-green-900/50 border border-green-700 rounded-lg p-6 sm:p-8 text-center"
                >
                  <CheckCircle2 className="text-green-400 mx-auto mb-3 sm:mb-4 sm:w-12 sm:h-12" size={40} />
                  <p className="text-green-200 text-base sm:text-lg font-semibold mb-2">Thank you!</p>
                  <p className="text-green-300 text-sm sm:text-base">Your message has been sent successfully. We'll get back to you soon.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5" noValidate>
                  {/* Error Message */}
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-red-900/50 border border-red-700 rounded-lg p-4 flex items-start gap-3"
                    >
                      <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
                      <p className="text-red-200 text-sm">{error}</p>
                    </motion.div>
                  )}

                  {/* Honeypot Field - Hidden from users, visible to bots */}
                  <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
                    <label htmlFor="website">Website (leave blank)</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={formData.honeypot}
                      onChange={(e) => setFormData({ ...formData, honeypot: e.target.value })}
                    />
                  </div>

                  <div>
                    <label htmlFor="name" className="block text-gray-300 mb-2 font-medium text-sm sm:text-base">
                      Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      minLength={2}
                      maxLength={100}
                      autoComplete="name"
                      className="w-full px-4 py-2.5 sm:py-3 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-bible-gold focus:outline-none focus:ring-2 focus:ring-bible-gold/50 text-sm sm:text-base transition-colors"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-gray-300 mb-2 font-medium text-sm sm:text-base">
                      Email <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                      className="w-full px-4 py-2.5 sm:py-3 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-bible-gold focus:outline-none focus:ring-2 focus:ring-bible-gold/50 text-sm sm:text-base transition-colors"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-gray-300 mb-2 font-medium text-sm sm:text-base">
                      Subject <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      minLength={3}
                      maxLength={200}
                      className="w-full px-4 py-2.5 sm:py-3 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-bible-gold focus:outline-none focus:ring-2 focus:ring-bible-gold/50 text-sm sm:text-base transition-colors"
                      placeholder="What is this about?"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-gray-300 mb-2 font-medium text-sm sm:text-base">
                      Message <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      minLength={10}
                      maxLength={2000}
                      rows={6}
                      className="w-full px-4 py-2.5 sm:py-3 bg-slate-700 text-white rounded-lg border border-slate-600 focus:border-bible-gold focus:outline-none focus:ring-2 focus:ring-bible-gold/50 resize-none text-sm sm:text-base transition-colors"
                      placeholder="Your message here..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.message.length}/2000 characters
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-3 sm:py-3.5 bg-bible-gold text-slate-900 rounded-lg font-semibold hover:bg-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 text-sm sm:text-base shadow-lg hover:shadow-xl"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send size={18} className="sm:w-5 sm:h-5" />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                  
                  <p className="text-xs sm:text-sm text-gray-500 text-center mt-4">
                    Required fields are marked with <span className="text-red-400">*</span>
                    <br />
                    <span className="text-gray-600">This form is protected against spam and abuse.</span>
                  </p>
                </form>
              )}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default Contact

